import {Box, Link, Typography} from '@mui/material';
import {PropsWithChildren, useEffect} from 'react';
import {palette} from 'lib/theme';
import {BoxProps} from '@mui/system';
import {Button} from 'components/Button';
import {PROSPER_SUPPORT_PHONE_NUMBER} from 'lib/utils/prosperDefaults';
import {useHubspotMutations} from 'lib/useHubspotMutations';

export type ErrorStateProps = PropsWithChildren<{
    title?: string;
    message?: string;
    variant?: 'default' | 'experiencingerror';
    onRetry?: () => void;
}> &
    BoxProps;

export function ErrorState({
    title,
    message,
    variant: variation = 'default',
    children,
    onRetry,
    sx,
    ...props
}: ErrorStateProps) {
    const {updateHubspotProsperDigitalAppStage} = useHubspotMutations();

    // Track when error is displayed
    useEffect(() => {
        updateHubspotProsperDigitalAppStage({stage: 'Prosper Digital Error Message Displayed'}).catch(console.error);
    }, [updateHubspotProsperDigitalAppStage]);
    return variation === 'experiencingerror' ? (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'start',
                justifyContent: 'left',
                flexDirection: 'column',
                maxWidth: '700px',
                mx: 'auto',
                mt: 10,
                ...sx,
            }}
            {...props}
        >
            <Box
                component="img"
                sx={(theme) => ({
                    [theme.breakpoints.up('sm')]: {maxHeight: '280px'},
                    maxWidth: '100%',
                    maxHeight: '180px',
                })}
                src="/images/experiencing_error.svg"
            />
            <Typography
                variant="h1"
                sx={(theme) => ({
                    [theme.breakpoints.up('xs')]: {fontSize: '26px'},
                    [theme.breakpoints.up('sm')]: {fontSize: '32px'},
                    lineHeight: '52.8px',
                    fontWeight: '400',
                    mt: 4,
                    opacity: 0.9,
                })}
            >
                We are experiencing an error.
            </Typography>
            <Typography
                variant="body2"
                sx={{color: palette.secondary[500], fontSize: '18px', fontWeight: '300', textAlign: 'left'}}
            >
                Please reload the page. If the problem persists, check back in a bit or give us a call at{' '}
                <Link
                    variant="body2"
                    href={`tel:1${PROSPER_SUPPORT_PHONE_NUMBER.replace(/\D/g, '')}`}
                    sx={{color: palette.primary[700]}}
                    underline="hover"
                >
                    {PROSPER_SUPPORT_PHONE_NUMBER}
                </Link>
                . We apologize for the inconvenience.
            </Typography>
        </Box>
    ) : (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexDirection: 'column',
                maxWidth: '391px',
                mx: 'auto',
                mt: {xs: 7, sm: 0},
                ...sx,
            }}
            {...props}
        >
            <Box
                component="img"
                sx={(theme) => ({
                    [theme.breakpoints.up('sm')]: {maxHeight: '280px'},
                    maxWidth: '100%',
                    maxHeight: '180px',
                })}
                src="/images/oops.svg"
            />
            <Typography
                variant="subtitle1"
                sx={(theme) => ({
                    [theme.breakpoints.up('xs')]: {fontSize: '26px'},
                    [theme.breakpoints.up('sm')]: {fontSize: '48px'},
                    fontWeight: '500',
                })}
            >
                {title ? title : <>oops</>}
            </Typography>
            <Typography variant="body2" sx={{color: palette.secondary[400], fontWeight: '300', textAlign: 'center'}}>
                {message || <>Something went wrong.</>}
            </Typography>
            <Button sx={{mt: '20px'}} onClick={onRetry} color="secondary" variant="contained" size="medium">
                Try Again
            </Button>
            {children && <Box sx={{mt: '53px'}}>{children}</Box>}
        </Box>
    );
}
